<template>
  <div
    ref="wrapperRef"
    class="wrapper"
  >
    <button
      v-if="hasMultipleSlides"
      ref="prevBtnRef"
      class="nav-arrow nav-prev icon-btn"
      aria-label="Previous"
      :disabled="isAnimating"
      @click="go(-1)"
    >
      <UIIconComponent icon="arrow-left" />
    </button>

    <div class="grid">
      <div
        ref="carouselRef"
        class="carousel"
      >
        <div
          ref="sceneRef"
          class="scene"
        >
          <div
            ref="cubeRef"
            class="cube"
          >
            <div
              ref="frontRef"
              class="face front"
            >
              <div class="face-content">
                <CubeFaceTitle
                  :title="frontTitle"
                  :subtitle="frontSubtitle"
                  :intro="frontIntro"
                  :link-object="frontLinkObject"
                  class="sm-face-title"
                />
                <BaseImage
                  v-if="leftImage"
                  :key="`front-media-${leftImageVersion}`"
                  :media-object="leftImage"
                  :sizes="{ xs: 800 }"
                  class="sm-face-image hide sm-show"
                />
              </div>
            </div>
            <div
              ref="backRef"
              class="face back"
            >
              <div class="face-content">
                <CubeFaceTitle
                  :title="backTitle"
                  :subtitle="backSubtitle"
                  :intro="backIntro"
                  :link-object="backLinkObject"
                  class="sm-face-title"
                />
                <BaseImage
                  v-if="rightImage"
                  :key="`back-media-${rightImageVersion}`"
                  :media-object="rightImage"
                  :sizes="{ xs: 800 }"
                  class="sm-face-image hide sm-show"
                />
              </div>
            </div>
            <div
              ref="rightRef"
              class="face right"
            >
              <BaseImage
                v-if="rightImage"
                :key="`right-${rightImageVersion}`"
                :media-object="rightImage"
                :sizes="{ md: 1920 }"
                class="face-image sm-hide"
              />
              <div class="hide sm-show bg-white" />
            </div>
            <div
              ref="leftRef"
              class="face left"
            >
              <BaseImage
                v-if="leftImage"
                :key="`left-${leftImageVersion}`"
                :media-object="leftImage"
                :sizes="{ md: 1920 }"
                class="face-image sm-hide"
              />
              <div class="hide sm-show bg-white" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <button
      v-if="hasMultipleSlides"
      ref="nextBtnRef"
      class="nav-arrow nav-next icon-btn"
      aria-label="Next"
      :disabled="isAnimating"
      @click="go(1)"
    >
      <UIIconComponent icon="arrow-right" />
    </button>
  </div>
</template>

<script setup lang="ts">
  import type { ElocusPage, BreadCrumb } from '@tc8l/nuxt-base'
  import type { HeroContent } from '@/types/contentblocks'
  import type { HeroSlideItem } from '@/types/heroSlider'
  import { useHeroCubeCarousel } from '@/composables/useHeroCubeCarousel'

  const props = defineProps<{
    content: HeroContent
    resolvedSlides?: HeroSlideItem[]
    breadCrumb?: BreadCrumb[]
    settings?: ElocusPage
    contentblockIdx?: number
  }>()

  const slides = computed(() => (props.resolvedSlides?.length ? props.resolvedSlides : props.content.slides || []))
  const hasMultipleSlides = computed(() => (props.resolvedSlides?.length ?? 0) > 1)

  const wrapperRef = ref(null)
  const carouselRef = ref(null)
  const sceneRef = ref(null)
  const cubeRef = ref(null)
  const frontRef = ref(null)
  const backRef = ref(null)
  const leftRef = ref(null)
  const rightRef = ref(null)
  const prevBtnRef = ref(null)
  const nextBtnRef = ref(null)

  const {
    frontTitle,
    frontSubtitle,
    leftImage,
    leftImageVersion,
    frontIntro,
    frontLinkObject,
    backTitle,
    backSubtitle,
    backIntro,
    backLinkObject,
    rightImage,
    rightImageVersion,
    isAnimating,
    go,
  } = useHeroCubeCarousel({
    slides,
    wrapperRef,
    carouselRef,
    sceneRef,
    cubeRef,
    frontRef,
    backRef,
    leftRef,
    rightRef,
    prevBtnRef,
    nextBtnRef,
  })
</script>

<style scoped lang="scss">
  .wrapper {
    position: relative;
    height: calc(100dvh - var(--header-height));
    max-height: 93rem;
    overflow: hidden;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .carousel {
    --carousel-padding-y: var(--margin-lg);
    --carousel-padding-x: 11rem;
    --viewport-safe-height: calc(100dvh - var(--header-height));
    --wrapper-height: min(var(--viewport-safe-height), 93rem);
    --visible-cube-height: calc(var(--wrapper-height) - (var(--carousel-padding-y) * 2));
    --box-width: min(calc(100dvw - var(--carousel-padding-x) * 4), 1112px);
    --box-base-width: 880;
    --box-depth: calc(var(--box-width) * 79 / 88);
    --box-perspective: calc(var(--box-width) * 1600 / var(--box-base-width));
    --face-padding: calc(var(--box-width) * 44 / var(--box-base-width));

    --cube-tilt: 38deg;
    --cube-depth-ratio: calc(79 / 88); // == --box-depth / --box-width, unitless
    --cube-perspective-ratio: calc(1600 / 880); // == --box-perspective / --box-width, unitless
    --cube-near-depth-ratio: calc((var(--cube-depth-ratio) / 2) * cos(var(--cube-tilt)) + 0.5 * sin(var(--cube-tilt)));
    --cube-perspective-scale: calc(
      var(--cube-perspective-ratio) / (var(--cube-perspective-ratio) - var(--cube-near-depth-ratio))
    );
    --box-height: calc(var(--visible-cube-height) / var(--cube-perspective-scale));

    margin-inline: auto;
    padding: var(--carousel-padding-y) 0;
  }

  .scene {
    width: var(--box-width);
    height: var(--box-height);
    max-height: var(--box-height);
    margin-inline: auto;
    perspective: var(--box-perspective);
    transform-origin: center;
    animation: grow-in 0.6s ease;
  }

  .nav-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    border-radius: 50%;
    background: #241c17;
    color: #fff;
    z-index: 2;
    transition: all 0.3s var(--ease-fast-in-slow-out);

    &:hover {
      transform: translateY(-50%) scale(1.08);
    }

    &:disabled {
      opacity: 0.8;
      cursor: default;
      pointer-events: none;
    }

    .icon {
      font-size: 2rem !important;
    }
  }

  .nav-prev {
    left: var(--margin-md);
  }

  .nav-next {
    right: var(--margin-md);
  }

  @keyframes grow-in {
    from {
      transform: scale(0.3);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  .cube {
    position: relative;
    width: 100%;
    height: 100%;
    transform-style: preserve-3d;
    will-change: transform;
  }

  .face {
    position: absolute;
    box-sizing: border-box;
    background: black;
    border-radius: 8px;
    backface-visibility: hidden;

    .face-content {
      border: 10px solid black;
      background: #f6eee9;
      position: absolute;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 100%;
      width: 100%;
      padding: var(--face-padding);
      border-radius: 18px;
      border-left: 7px solid black;
      overflow: hidden;
    }
  }

  .front,
  .back {
    width: var(--box-width);
    height: var(--box-height);
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .front {
    transform: translateZ(calc(var(--box-depth) / 2));
  }

  .back {
    transform: rotateY(180deg) translateZ(calc(var(--box-depth) / 2));
  }

  .right,
  .left {
    width: var(--box-depth);
    height: var(--box-height);
    left: calc((var(--box-width) - var(--box-depth)) / 2);
    overflow: hidden;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-left: 6px solid black;

    .bg-white {
      width: 100%;
      height: 100%;
      background-color: #f6eee9;
      overflow: hidden;
      border: 12px black solid;
      border-left: 20px black solid;
      border-right: 1px;
      border-radius: 25px;
    }
  }

  .right {
    transform: rotateY(90deg) translateZ(calc(var(--box-width) / 2));
  }

  .left {
    transform: rotateY(-90deg) translateZ(calc(var(--box-width) / 2));
  }

  .face-image {
    width: 100%;
    height: 100%;
    border-radius: 28px;
    overflow: hidden;
    border: 10px solid black;
    border-right: 7px solid black;
  }

  .face-image,
  .sm-face-image {
    :deep(.image-wrapper) {
      width: 100%;
      height: 100%;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
  }

  @include media(md) {
    .carousel {
      --box-width: min(calc(100vw - var(--carousel-padding-x) * 3), 1112px);
    }
  }

  @include media(sm) {
    .nav-prev {
      left: var(--margin-xs);
    }

    .nav-next {
      right: var(--margin-xs);
    }

    .carousel {
      --box-width: min(calc(100vw - 4rem), 40rem);
      --cube-tilt: 25deg; // REST_TILT_MOBILE in useHeroCubeCarousel.ts
    }

    .face .face-content {
      border: 6px solid black;
      overflow: hidden;
      gap: var(--margin-md);
    }

    .sm-face-image {
      flex: 1;
      min-height: 0;
      max-height: 45%;
      margin: 0 calc(var(--face-padding) * -1) calc(var(--face-padding) * -1);
    }
  }

  @media (max-height: 780px) and (min-width: #{$screen-sm + 1px}) {
    .face .face-content {
      padding: calc(var(--face-padding) * 0.9);
    }
  }
</style>

<style lang="scss">
  // Unscoped on purpose: useHeroCubeCarousel.ts's playSpin() needs to access it
  @keyframes cube-spin {
    0% {
      transform: translateX(var(--spin-shift)) rotateY(var(--spin-from));
    }
    2% {
      transform: translateX(var(--spin-shift)) rotateY(var(--spin-early));
    }
    100% {
      transform: translateX(var(--spin-shift)) rotateY(var(--spin-to));
    }
  }
</style>
